import { Tag } from 'antd';
import { motion } from 'framer-motion';
import { FiAlertCircle, FiDownload, FiFileText, FiLoader } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { EXPORT_STATE_TAG, useExportDownload } from '../../hooks/useExportDownload';
import { DataExportCompletedNotificationType, DataExportFailedNotificationType } from '../../types/notifications';
import { LIST_ITEM_ANIMATIONS } from '../consts/motionAnimations';
import { inDisplayZone } from '@app/utils/dates/displayTimezone';

type DataExportWidgetNotificationCardProps = {
  notificationDetails: DataExportCompletedNotificationType | DataExportFailedNotificationType;
  onClick: (id: number) => void;
};

const ICON_CLASS = {
  ready: 'bg-green-100 text-green-600',
  failed: 'bg-red-100 text-red-600',
  empty: 'bg-slate-100 text-slate-500',
  unavailable: 'bg-slate-100 text-slate-500',
};

const DataExportWidgetNotificationCard = ({ notificationDetails, onClick }: DataExportWidgetNotificationCardProps) => {
  const { t } = useTranslation();
  const { state, available, downloading, download } = useExportDownload(notificationDetails);
  const tag = EXPORT_STATE_TAG[state];
  const markRead = () => onClick(notificationDetails.notificationId);

  // Only a finished or no-longer-available download marks it read: a failed one stays for a retry.
  const handleClick = async () => {
    if (!available) {
      markRead();
      return;
    }
    const outcome = await download();
    if (outcome === 'downloaded' || outcome === 'unavailable') markRead();
  };

  return (
    <motion.li key={notificationDetails.notificationId} className="overflow-hidden" {...LIST_ITEM_ANIMATIONS}>
      <div className="group flex flex-col px-4 py-3 hover:bg-slate-50 cursor-pointer">
        <div onClick={handleClick} className="flex w-full items-start gap-3 text-left">
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${ICON_CLASS[state]}`}>
            {state === 'ready' && !downloading && <FiDownload fontSize={16} />}
            {downloading && (
              <FiLoader fontSize={16} className="animate-spin" role="img" aria-label={t('notifications-downloading')} />
            )}
            {state === 'failed' && <FiAlertCircle fontSize={16} />}
            {(state === 'empty' || state === 'unavailable') && <FiFileText fontSize={16} />}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between">
              <p className="truncate text-sm font-semibold text-slate-800">{notificationDetails.title}</p>
              <time className="ml-2 flex-shrink-0 text-xs text-slate-500">
                {inDisplayZone(notificationDetails.createdAt).fromNow()}
              </time>
            </div>
            <p className="mt-1 text-xs text-slate-600 line-clamp-2">{notificationDetails.message}</p>

            <div className="mt-2 flex items-center gap-2">
              <Tag color={tag.color} className="text-xs">
                {t(tag.labelKey)}
              </Tag>
              {available && <span className="text-xs text-blue-600">{t('notifications-click-to-download')}</span>}
              {!notificationDetails.isRead && (
                <div className="h-2 w-2 rounded-full bg-blue-500" aria-label={t('notifications-new-badge')} />
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.li>
  );
};

export default DataExportWidgetNotificationCard;
